import { useSyncExternalStore } from 'react';

import { getUserIdOrNull } from '@/lib/pwa/userScope';

/**
 * Qual Secretário a tela está mostrando (docs/PLANO_SECRETARIO_ESCRITORIO.md §6): o
 * pessoal ou o de um escritório. Vale para as chamadas da API (``configuracao_id``)
 * e para as chaves de cache — dois Secretários nunca dividem o mesmo cache.
 *
 * Guardado por usuário (``userScope``): reabrir o app volta ao último escolhido.
 */
const listeners = new Set<() => void>();
let cache: { usuario: string | null; id: string | null } | null = null;

function chave(usuario: string | null): string | null {
  return usuario ? `jurisapp:${usuario}:secretario:configuracao` : null;
}

function ler(usuario: string | null): string | null {
  const k = chave(usuario);
  if (!k) return null;
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}

export function getConfiguracaoAtual(): string | null {
  const usuario = getUserIdOrNull();
  if (cache === null || cache.usuario !== usuario) {
    cache = { usuario, id: ler(usuario) };
  }
  return cache.id;
}

export function setConfiguracaoAtual(id: string | null): void {
  const usuario = getUserIdOrNull();
  if (getConfiguracaoAtual() === id) return;
  cache = { usuario, id };
  const k = chave(usuario);
  if (k) {
    try {
      if (id) localStorage.setItem(k, id);
      else localStorage.removeItem(k);
    } catch {
      // Sem storage (aba privada): vale só nesta sessão.
    }
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A configuração escolhida, reativa: quem a usa re-renderiza na troca. */
export function useConfiguracaoAtual(): string | null {
  return useSyncExternalStore(subscribe, getConfiguracaoAtual, getConfiguracaoAtual);
}

/** O segmento das chaves de cache do Secretário: um por configuração. */
export function escopoDoCache(): string {
  return getConfiguracaoAtual() ?? 'padrao';
}
